import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import isMobile from "ismobilejs";
import React from "react";
import styled, { css, ThemeConsumer, ThemeProps, withTheme } from "styled-components";

export interface MappedLink {
    // expanded from content-library/src/widgets/header/MainNavigation.tsx
    title: string;
    url?: string;
    type?: string;
    clickableProps?: ClickableProps;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    smallImagePath?: string;
    childrenType?: "MegaMenu" | "CascadingMenu" | "CustomLinkList";
    children?: MappedLink[];
    excludeFromNavigation?: boolean;
}

export interface MenuPresentationProps {
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        menuTrigger: React.ReactElement;
        /** Width of the menu and submenus in pixels. */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: true;
        /** Session boolean value that decides whether to show Change Customer Link */
        displayChangeCustomerLink?: boolean;
        /** Added children prop to additional links to Menu at bottom */
        children?: React.ReactNode;
    }
>;

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
export type MenuProps = MenuComponentProps & MenuPresentationProps & ThemeProps<BaseTheme>;

type MenuStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
    };

// eslint-disable-next-line no-unexpected-multiline
const MenuStyle = styled.ul<MenuStyleProps>`
background-color: ${getColor("common.background")};
z-index: ${getProp("theme.zIndex.menu")};
    box-shadow: ${getProp("theme.shadows.2")};
    width: ${getProp("width", 175)}px;
    list-style: none;
    position: absolute;
    margin: 0;
    padding: 0;
    display: none;
    ${injectCss}
    &::before {
        content: "";
        width: 0;
        height: 0;
        border-left: 15px solid transparent;
        border-right: 15px solid transparent;
        border-bottom: 15px solid rgba(244, 246, 250, 1);
        position: absolute;
        top: -15px;
        left: 35px;
    }
    a {
        display: flex;
        align-items: center;
        position: relative;
        border: 1px solid #d8dee5;
        &:hover {
            background: #f4f4f4 !important;
        }
        svg {
            width: 20px;
            height: 20px;
        }
    }

    &.dc-vertical-submenu {
        width: ${getProp("width", 175)}px;
        padding: 0;
        box-shadow: none;

        &::before {
            display: none;
        }

        > li {
            width: 100%;
            margin-right: 0;
        }

        > li > a {
            min-height: 42px;
            justify-content: space-between;
            background: ${getColor("common.background")};
        }

        > li > a .menu-item-text {
            font-family: "Poppins";
            color: ${getColor("secondary")};
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 1.2;
            text-align: left;
            text-transform: none;
            padding: 10px 15px 10px 20px;
            width: calc(100% - 24px);
            flex: 0 0 calc(100% - 24px);
            box-sizing: border-box;
        }

        > li > a > svg {
            flex: 0 0 20px;
            margin-right: 8px;
            color: ${getColor("primary")};
        }

        > li > ul {
            display: none;
        }
    }

    &.image-grid-submenu {
        border: 0.25px solid #f4f4f4;
        padding: 10px;
        clip-path: polygon(0 0, 0 100%, 100% 100%, 100% 100%, 100% 0);
        width: 592px;
        align-items: start;

        > li {
            width: 126px;
            margin-right: 12px;
        }

        > li > a {
            display: flex;
            flex-direction: column;
            border: none;
        }

        > li > a:hover {
            background: none !important;
        }

        > li > a .menu-item-text {
            font-family: "Poppins";
            color: ${COLORS.black};
            font-size: ${TYPOGRAPHY.size.smallest};
            font-weight: ${TYPOGRAPHY.weight.base};
            line-height: 15px;
            text-align: center;
            text-transform: capitalize;
            padding: 8px;
            width: 100%;
        }

        > li > ul {
            display: none;
        }
    }
`;

type MenuWrapperProps = { canBeOpen: boolean; isOpen?: true } & InjectableCss;

const MenuWrapper = styled.nav<MenuWrapperProps>`
    position: relative; /* stylelint-disable */
    & > span {
        &:hover {
            span {
                color: ${COLORS.white};
            }
            svg {
                color: ${COLORS.white};
            }
        }
    }

    ${({ isOpen }) =>
        isOpen &&
        `& > ${MenuStyle} {
                display: block;
               
            }
    `}
    ${({ canBeOpen }) =>
        canBeOpen &&
        `
    &:focus-within > ${MenuStyle}  /* stylelint-enable */ {
        display: block;
    }
    .trigger:focus + ${MenuStyle},
    &:hover > ${MenuStyle},
    &.focus-within > ${MenuStyle} {
        display: block;
    }`}
    
    ${injectCss}
`;

// eslint-disable-next-line no-unexpected-multiline
type MenuItemStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        onClick: () => void;
        submenuTopOffset?: number;
    };

const MenuItemStyle = styled.li<MenuItemStyleProps>`
    position: relative;

    &:focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0px;
    }

    &:focus > ${MenuStyle}, &:hover > ${MenuStyle}, &.focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0px;
    }

    &:focus > ${MenuStyle}.image-grid-submenu,
    &:hover > ${MenuStyle}.image-grid-submenu,
    &.focus-within > ${MenuStyle}.image-grid-submenu {
        display: flex;
        flex-wrap: wrap;
        min-height: 100%;
    }

    a {
        width: 100%;
        min-height: 42px;
        justify-content: space-between;
        span {
            svg {
                color: ${getColor("primary")} !important;
            }
        }
        &:hover {
            background: ${getColor("common.border")};
            span {
                color: ${getColor("primary")} !important;
            }
        }
    }

    ${injectCss}
`;

const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean }>`
    padding: 10px 15px 10px 20px;
    font-family: "Poppins";
    color: ${getColor("secondary")};
    font-size: ${TYPOGRAPHY.size.smaller};
    font-weight: ${TYPOGRAPHY.weight.mediumbold};
    letter-spacing: 1.5px;
    display: block;
    box-sizing: border-box;
    ${({ hasChildren }: { hasChildren?: boolean }) => hasChildren && "width: calc(100% - 24px);"}
`;

const trigger = React.createRef<HTMLElement>();

interface MenuState {
    canBeOpen: boolean;
    isMobile: boolean;
    shouldRender: boolean;
}

/**
 * A cascading menu that renders a defined number of sub-menus.
 */
class MenuForDcProduct extends React.Component<MenuProps, MenuState> {
    static defaultProps: Partial<MenuProps> = {
        maxDepth: 3,
        width: 175,
    };

    state: MenuState = {
        canBeOpen: true,
        isMobile: false,
        shouldRender: true,
    };

    componentDidMount() {
        this.setState({
            isMobile: isMobile().any,
        });

        window.addEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });
    }

    componentWillUnmount() {
        window.removeEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });
    }

    closeMenu = () => {
        // On Mobile, the below logic
        // where we switch the canBeOpen state from false to true
        // causes the mobile experience to not function well
        // due to Mobile not doing hover functionality

        // Solution:
        // I prevent the boolean toggle when we detect mobile

        this.setState({ canBeOpen: this.state.isMobile }, () =>
            setTimeout(() => {
                // The make the all browsers act the same on item click.
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                this.setState({ canBeOpen: true });
            }, 100),
        );
    };

    closeMenuWithoutChildren = (hasMenuChildren?: boolean) => {
        // This is for high resolution mobile users.
        // So that the menu closes on nav items without children
        if (!hasMenuChildren) {
            this.setState(
                {
                    shouldRender: false,
                },
                () => {
                    this.setState({ shouldRender: true });
                },
            );
        }
    };

    render() {
        return (
            <ThemeConsumer>
                {theme => {
                    const { menuItems, menuTrigger, isOpen, ...otherProps } = this.props;
                    const { applyProp, spreadProps } = applyPropBuilder(
                        { ...otherProps, theme },
                        { component: "menu" },
                    );

                    const renderMenuItems = (childMenuItems: MappedLink[], currentDepth: number, isInDCBranch = false) => {
                        const cssOverrides = spreadProps("cssOverrides");
                        const maxDepth = otherProps.maxDepth || 3;
                        const isDCChildMenu = isInDCBranch && currentDepth === 2;
                        const isImageGridMenu = (!isInDCBranch && currentDepth === 2) || (isInDCBranch && currentDepth === 3);
                        const menuClassName = `mainMenu${isDCChildMenu ? " dc-vertical-submenu" : ""}${isImageGridMenu ? " image-grid-submenu" : ""}`;
                        
                        return (
                            <MenuStyle
                                width={otherProps.width}
                                css={cssOverrides?.menu}
                                className={menuClassName}
                            >
                                {currentDepth === 1 && (
                                    <Clickable
                                        href="/MyAccount"
                                        className="myAccountSection"
                                        css={css`
                                            display: flex;
                                            min-height: 40px;
                                            -webkit-box-pack: center;
                                            justify-content: center;
                                            color: ${getColor("secondary")};
                                            padding: 10px;
                                            font-size: ${TYPOGRAPHY.size.smaller};
                                            font-family: ${TYPOGRAPHY.fontFamily.base};
                                            background: transparent !important;
                                            font-weight: ${TYPOGRAPHY.weight.mediumbold};
                                            letter-spacing: 1.5px;
                                            &:hover {
                                                color: ${getColor("primary")} !important;
                                            }
                                        `}
                                    >
                                        {translate("My Account")}
                                    </Clickable>
                                )}
                                {childMenuItems
                                    ?.filter(item => !item.excludeFromNavigation)
                                    .map((item, index) => {
                                        const hasChildren =
                                            item.children?.some(o => !o.excludeFromNavigation) && currentDepth < maxDepth;
                                        const showChevron = hasChildren && !isImageGridMenu;
                                        
                                        if (
                                            (item.type === "ChangeCustomerPage" &&
                                                !this.props.displayChangeCustomerLink) ||
                                            !this.state.shouldRender
                                        ) {
                                            return null;
                                        }

                                        const isDCAtTopLevel = currentDepth === 1 && item.title.trim().toLowerCase() === "dc powered";
                                        const nextIsInDCBranch = isInDCBranch || isDCAtTopLevel;
                                        const submenuTopOffset = currentDepth === 1 ? -Math.round(index * 42) : 0;

                                        return (
                                            <MenuItemStyle
                                                width={otherProps.width}
                                                css={cssOverrides?.menuItem}
                                                key={`${item.title}_${index}`}
                                                data-test-selector="menuItem"
                                                onClick={() => {
                                                    this.closeMenuWithoutChildren(hasChildren);
                                                }}
                                                className="menuItem"
                                                submenuTopOffset={submenuTopOffset}
                                            >
                                                <Clickable
                                                    href={item.url}
                                                    onClick={this.closeMenu}
                                                    target={item.openInNewWindow ? "_blank" : ""}
                                                    {...applyProp("menuItemClickableProps")}
                                                >
                                                    {isImageGridMenu && item.smallImagePath && (
                                                        <LazyImage
                                                            src={item.smallImagePath}
                                                            altText={item.title}
                                                            css={css`
                                                                max-height: 85px;
                                                                img {
                                                                    aspect-ratio: 12/8;
                                                                    object-fit: cover;
                                                                    border-radius: 5px;
                                                                }
                                                            `}
                                                        />
                                                    )}
                                                    <MenuItemText
                                                        className="menu-item-text"
                                                        hasChildren={hasChildren}
                                                        {...applyProp("menuItemTypographyProps")}
                                                    >
                                                        {item.title}
                                                    </MenuItemText>
                                                    {showChevron && <Icon src={ChevronRight} />}
                                                </Clickable>
                                                {hasChildren
                                                    ? renderMenuItems(item!.children!, currentDepth + 1, nextIsInDCBranch)
                                                    : null}
                                            </MenuItemStyle>
                                        );
                                    })}
                                {this.props.children}
                            </MenuStyle>
                        );
                    };

                    const cssOverrides = spreadProps("cssOverrides");
                    const descriptionId = otherProps.descriptionId || uniqueId();
                    return (
                        <>
                            <MenuWrapper
                                canBeOpen={this.state.canBeOpen}
                                isOpen={isOpen}
                                css={cssOverrides?.wrapper}
                                {...{ "aria-labelledby": descriptionId }}
                                {...otherProps}
                            >
                                {React.cloneElement(menuTrigger, { id: descriptionId, className: "trigger" } as any)}
                                
                                {renderMenuItems(menuItems, 1)}
                            </MenuWrapper>
                            <span ref={trigger} tabIndex={-1} />
                        </>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/** @component */
export default withTheme(MenuForDcProduct);

export { MenuItemText };
